import { HttpClient, robotoHeaders } from "@/shared/http";

import type { AdminRegisterMcpServerRequest } from "./hooks/useAdminRegisterMcpServer";
import {
  DiscoveredMcpTool,
  McpOrgContextRecord,
  McpServerRecord,
  McpTokenRecord,
  OAuthCallbackRequest,
  RegisterMcpServerRequest,
  StartOAuthFlowResponse,
} from "./McpServerRecord";

interface Options {
  abortSignal?: AbortSignal;
}

export class McpRegistryService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async registerServer(
    request: RegisterMcpServerRequest,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/servers");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<McpServerRecord>();
  }

  public async listServers(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpServerRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/servers");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<McpServerRecord[]>();
  }

  public async getServer(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<McpServerRecord>();
  }

  public async startOAuthFlow(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<StartOAuthFlowResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}/oauth/start`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<StartOAuthFlowResponse>();
  }

  public async oauthCallback(
    request: OAuthCallbackRequest,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpTokenRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/oauth/callback");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<McpTokenRecord>();
  }

  public async deleteServer(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}`,
    );
    await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
  }

  public async disconnectServer(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}/oauth/token`,
    );
    await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
  }

  public async listTokenStatuses(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpTokenRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/tokens");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<McpTokenRecord[]>();
  }

  public async getOrgContext(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<McpOrgContextRecord | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}/context`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<McpOrgContextRecord | null>();
  }

  public async setOrgContext(
    serverId: string,
    orgId: string,
    context: string,
    options?: Partial<Options>,
  ): Promise<McpOrgContextRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/servers/${serverId}/context`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify({ context }),
    });
    return response.json<McpOrgContextRecord>();
  }

  public async adminListServers(
    options?: Partial<Options>,
  ): Promise<McpServerRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/admin/servers");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<McpServerRecord[]>();
  }

  public async adminRegisterServer(
    request: AdminRegisterMcpServerRequest,
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/mcp/admin/servers");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<McpServerRecord>();
  }

  public async adminUpdateServerOrgs(
    serverId: string,
    orgIds: string[],
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/admin/servers/${serverId}/orgs`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({ org_ids: orgIds }),
    });
    return response.json<McpServerRecord>();
  }

  public async adminDeleteServer(
    serverId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/admin/servers/${serverId}`,
    );
    await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
    });
  }

  public async adminUpdateAllowedTools(
    serverId: string,
    allowedTools: Record<string, boolean> | null,
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/admin/servers/${serverId}/allowed-tools`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({ allowed_tools: allowedTools }),
    });
    return response.json<McpServerRecord>();
  }

  public async adminCopyAllowedTools(
    serverId: string,
    sourceServerId: string,
    options?: Partial<Options>,
  ): Promise<McpServerRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/admin/servers/${serverId}/copy-allowed-tools`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify({ source_server_id: sourceServerId }),
    });
    return response.json<McpServerRecord>();
  }

  public async adminDiscoverTools(
    serverId: string,
    orgId: string,
    options?: Partial<Options>,
  ): Promise<DiscoveredMcpTool[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/mcp/admin/servers/${serverId}/discover-tools`,
      new URLSearchParams({ org_id: orgId }),
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<DiscoveredMcpTool[]>();
  }
}
